/**
 * Shared Scan Dropdown Component
 *
 * Provides consistent "Scan" dropdown across sections with hybrid pattern support:
 *
 * Pattern 1: Handler-Based (Vulnerabilities - 2-3 scan types)
 * - Simple sections pass individual handlers for each scan type
 * - Component builds options internally from handlers
 *
 * Pattern 2: Options-Based (Assets - 6+ scan types)
 * - Complex sections build options externally using option builders
 * - Component receives pre-built options for maximum flexibility
 *
 * Sections control business logic through handler props or pre-built options.
 * Component handles UI presentation and consistency.
 *
 * @example
 * ```tsx
 * // Pattern 1: Handler-based (Vulnerabilities)
 * <ScanDropdown
 *   onRescanClick={() => handlers.handleRescan(vulnerabilities)}
 *   onAffiliationClick={() => handlers.handleConfirmAffiliation(vulnerabilities)}
 *   disabledActions={{ affiliation: !isPraetorianUser }}
 * />
 *
 * // Pattern 2: Options-based (Assets - future migration)
 * <ScanDropdown
 *   scanOptions={getAssetScanOptions({
 *     assets,
 *     handlers,
 *     selectUserAgent,
 *     isPraetorianUser
 *   })}
 * />
 *
 * // With custom labels and shortcuts
 * <ScanDropdown
 *   labels={{ rescan: 'Re-scan Assets', affiliation: 'Verify Ownership' }}
 *   shortcuts={{ rescan: '⌘ R' }}
 *   onRescanClick={handlers.handleRescan}
 *   onAffiliationClick={handlers.handleConfirmAffiliation}
 * />
 *
 * // Hide specific actions
 * <ScanDropdown
 *   hideActions={{ affiliation: true }}
 *   onRescanClick={handlers.handleRescan}
 *   onAffiliationClick={() => {}} // Still required but won't render
 * />
 * ```
 *
 * @module components/actions
 */

import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { useMemo } from 'react';

import { ACTION_ICONS } from '@/components/actions/constants/actionIcons';
import type { ScanDropdownProps } from '@/components/actions/types';
import { Dropdown } from '@/components/Dropdown';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';

/**
 * Default labels for scan actions
 */
const DEFAULT_LABELS = {
  rescan: 'Rescan Affected Assets',
  affiliation: 'Confirm Affiliation',
  comprehensive: 'Comprehensive Scan',
  network: 'Network Scan',
  port: 'Port Scan',
  crawl: 'Web Crawl',
  webscan: 'Web Scan (DAST)',
} as const;

/**
 * Scan Dropdown Component
 *
 * Renders a dropdown with scan actions using either:
 * - Handler-based pattern: Individual handlers for simple sections
 * - Options-based pattern: Pre-built options for complex sections
 *
 * Supports customization via labels, shortcuts, hideActions, and disabledActions.
 *
 * @param props - Component props
 * @returns Dropdown component
 */
export function ScanDropdown({
  onRescanClick,
  onAffiliationClick,
  onComprehensiveScan,
  onAffiliationScan,
  onNetworkScan,
  onPortScan,
  onCrawlScan,
  onWebScan,
  scanOptions,
  labels,
  shortcuts,
  hideActions,
  disabledActions,
  label = 'Scan',
  disabled = false,
  className,
  'data-testid': testId = 'scan-dropdown',
}: ScanDropdownProps) {
  /**
   * Build dropdown options based on configuration
   *
   * Memoized to prevent unnecessary re-renders when handlers change.
   * Supports both handler-based and options-based patterns.
   *
   * Handler-based pattern (Vulnerabilities):
   * - Component builds options from individual handlers
   * - Simple sections with 2-3 scan types
   *
   * Options-based pattern (Assets):
   * - Pre-built options passed in
   * - Complex sections with 6+ scan types
   */
  const options = useMemo<DropdownOption[]>(() => {
    // Pattern 2: If pre-built options provided (Assets pattern)
    if (scanOptions) {
      return scanOptions.map(opt => ({
        ...opt,
        disabled: opt.disabled || disabled,
      }));
    }

    // Pattern 1: Build from handlers (Vulnerabilities & Seeds pattern)
    const items: DropdownOption[] = [];

    // Rescan action (Vulnerabilities)
    if (onRescanClick && !hideActions?.rescan) {
      items.push({
        label: labels?.rescan ?? DEFAULT_LABELS.rescan,
        icon: <ACTION_ICONS.RESCAN className="size-4" />,
        onClick: onRescanClick,
        disabled: disabled || disabledActions?.rescan,
        count: shortcuts?.rescan,
      });
    }

    // Affiliation action (Vulnerabilities)
    if (onAffiliationClick && !hideActions?.affiliation) {
      items.push({
        label: labels?.affiliation ?? DEFAULT_LABELS.affiliation,
        icon: <ACTION_ICONS.AFFILIATION className="size-4" />,
        onClick: onAffiliationClick,
        disabled: disabled || disabledActions?.affiliation,
        count: shortcuts?.affiliation,
      });
    }

    // Comprehensive scan (Seeds)
    if (onComprehensiveScan && !hideActions?.comprehensive) {
      items.push({
        label: labels?.comprehensive ?? DEFAULT_LABELS.comprehensive,
        icon: <ACTION_ICONS.COMPREHENSIVE_SCAN className="size-4" />,
        onClick: onComprehensiveScan,
        disabled: disabled || disabledActions?.comprehensive,
        count: shortcuts?.comprehensive,
      });
    }

    // Affiliation scan (Seeds)
    if (onAffiliationScan && !hideActions?.affiliation) {
      items.push({
        label: labels?.affiliation ?? DEFAULT_LABELS.affiliation,
        icon: <ACTION_ICONS.AFFILIATION className="size-4" />,
        onClick: onAffiliationScan,
        disabled: disabled || disabledActions?.affiliation,
        count: shortcuts?.affiliation,
      });
    }

    // Network scan (Seeds)
    if (onNetworkScan && !hideActions?.network) {
      items.push({
        label: labels?.network ?? DEFAULT_LABELS.network,
        icon: <ACTION_ICONS.NETWORK_SCAN className="size-4" />,
        onClick: onNetworkScan,
        disabled: disabled || disabledActions?.network,
        count: shortcuts?.network,
      });
    }

    // Port scan (Seeds)
    if (onPortScan && !hideActions?.port) {
      items.push({
        label: labels?.port ?? DEFAULT_LABELS.port,
        icon: <ACTION_ICONS.PORT_SCAN className="size-4" />,
        onClick: onPortScan,
        disabled: disabled || disabledActions?.port,
        count: shortcuts?.port,
      });
    }

    // Add divider before web scans if we have items and web scans
    if (items.length > 0 && (onCrawlScan || onWebScan)) {
      items.push({ label: '', onClick: () => {}, isDivider: true });
    }

    // Web crawl (Seeds)
    if (onCrawlScan && !hideActions?.crawl) {
      items.push({
        label: labels?.crawl ?? DEFAULT_LABELS.crawl,
        icon: <ACTION_ICONS.WEB_CRAWL className="size-4" />,
        onClick: onCrawlScan,
        disabled: disabled || disabledActions?.crawl,
        count: shortcuts?.crawl,
      });
    }

    // Web scan (Seeds)
    if (onWebScan && !hideActions?.webscan) {
      items.push({
        label: labels?.webscan ?? DEFAULT_LABELS.webscan,
        icon: <ACTION_ICONS.WEB_SCAN className="size-4" />,
        onClick: onWebScan,
        disabled: disabled || disabledActions?.webscan,
        count: shortcuts?.webscan,
      });
    }

    // Empty state - show disabled message if no actions available
    if (items.length === 0) {
      return [
        {
          label: 'No scans available',
          onClick: () => {},
          disabled: true,
        },
      ];
    }

    return items;
  }, [
    scanOptions,
    onRescanClick,
    onAffiliationClick,
    onComprehensiveScan,
    onAffiliationScan,
    onNetworkScan,
    onPortScan,
    onCrawlScan,
    onWebScan,
    labels,
    shortcuts,
    hideActions,
    disabledActions,
    disabled,
  ]);

  return (
    <div data-testid={testId}>
      <Dropdown
        title="Scan Options"
        size="sm"
        options={options}
        placeholder={label}
        styleType="rounded"
        border={false}
        disabled={disabled}
        className={cn('[&_span:nth-child(3)]:hidden', className)}
        listClassName="!z-[150]"
        icon={<EllipsisHorizontalCircleIcon className="size-4" />}
      />
    </div>
  );
}
